import { Link } from 'react-router'

import { PageHeader } from '../components/PageHeader'
import { Avatar, Button, Card, Icon, Pill, StatusPill } from '../components/ui'
import { useAuth } from '../contexts/AuthContext'
import { DATA_RIGHTS_ENABLED } from '../lib/features'
import { formatDateTime } from '../lib/format'

/**
 * The account screen (§6.1) — the profile facts from `/me`, and nothing else.
 *
 * Until Sprint 1.9 this was also the landing page, and it said in as many words
 * that "the recording and Daily Status screens arrive in the next release". They
 * have: `/today` is where a non-admin now lands, and that sentence has gone with
 * it rather than being left to age into a falsehood.
 *
 * **It says nothing about export or deletion**, which is §6.2's instruction and
 * not an oversight. `POST /me/export` today creates a job that terminates
 * `failed` with `feature.not_implemented`, and a data-rights affordance that
 * reliably fails is worse than no affordance. Silence is the honest state, and
 * `src/test/dataRightsDark.test.ts` measures it against a real production
 * bundle rather than trusting this comment. (An earlier version of this comment
 * said those screens were "built and flag-gated off" while nothing imported the
 * flag at all — true in advance is another word for wrong.)
 *
 * **Every value here already sits in `useAuth().account`**, so this screen
 * issues no API call of its own. That is asserted against `fetch` rather than
 * against the client, because the claim is about what leaves the browser.
 *
 * ── THE SHAPE, FROM SPRINT 1.9.1 (R26) ──────────────────────────────────────
 *
 * The email is the screen's NAME rather than a row in the grid, beside the
 * avatar and the two badges that describe the account; the grid keeps the four
 * remaining facts. Both facts that moved are still stated exactly once — that
 * matters to more than tidiness, because `AccountHome.test.tsx:42-46` reaches
 * for the email with `getByText`, which throws on a second match.
 *
 * Sign-out gets a row of its own with a sentence saying what it does and does
 * not touch. `signOut` is a local Amplify sign-out plus `clearAllPendingJobs()`,
 * `clearAttempts()` and `clearSessionCache()` (`AuthContext.tsx:313-330`): the
 * upload resume pointer, Beacon's check-in attempts and Home's in-memory answers,
 * in this browser, and nothing on the server.
 */
export function AccountHome() {
  const { account, signOut } = useAuth()

  return (
    <div className="stack stack--reading account">
      {/* `eyebrowHidden` because the kicker repeats the `<h1>` (R25): it is the
          design's rhythm, not a second name for the screen.

          The lead is NOT "your account is approved and ready", which was a
          client-authored status claim -- true only because `RequireAuth`
          guarantees it, which is the "true because something upstream
          guarantees it" reasoning the StatusPill below deliberately does not
          rely on. One statement of account state per screen, and it is the one
          from `/me`. */}
      <PageHeader
        eyebrow="Account"
        eyebrowHidden
        title="Your account"
        lead="Your profile, as BrainSpot holds it."
      />

      {/* NO EYEBROW (R25): the screen's one cyan kicker is the page header's,
          and a card kicker repeating the word under it would be a second. */}
      <Card>
        <div className="account__who">
          <Avatar email={account?.email ?? null} size="lg" />
          <div className="account__id">
            <span className="account__name">{account?.email ?? '—'}</span>
            <div className="detail-badges">
              {/* §6.1 asks for the approval STATUS, not only the date it was
                  granted. Inside the console it is always `active` --
                  `RequireAuth` renders `AccountStatusScreen` instead of this for
                  every other value -- and showing it anyway is what the spec
                  asks for and is the honest thing besides: a person looking for
                  the state of their account should find it stated, not inferred
                  from the absence of a warning. */}
              {account && <StatusPill status={account.status} />}
              {/* The payload's own string, unmapped. */}
              {account && <Pill>{account.role}</Pill>}
            </div>
          </div>
        </div>
        <dl className="detail-grid">
          <Detail label="Email verified" value={account?.email_verified ? 'Yes' : 'No'} />
          <Detail label="Member since" value={formatDateTime(account?.created_at)} />
          <Detail label="Approved" value={formatDateTime(account?.approved_at)} />
          <Detail label="Account id" value={account?.user_id ?? '—'} mono />
        </dl>
      </Card>

      {/* §6.2: until Sprint 1.11 makes export and deletion work, this screen says
          nothing about either. The guard is a build-time constant, so with the
          flag off this block is not hidden -- it is not in the bundle. */}
      {DATA_RIGHTS_ENABLED && (
        <Card eyebrow="Your data">
          <p>A copy of everything we hold about you, and how to have it erased.</p>
          <p>
            <Link to="/account/data">Export or delete your data</Link>
          </p>
        </Card>
      )}

      {/* Sign-out lives in the shell footer too -- but `AppShell` renders that
          footer only inside the desktop rail and inside the phone drawer, and
          the rail is `isDesktop &&`. So on a phone there is no way off this
          screen without first opening the navigation. §6.1 names sign-out as
          part of this surface; `AccountStatus.tsx` already gives a screen its
          own. */}
      <Card>
        <div className="account__row">
          <div className="account__row-text">
            <span className="account__row-title">Sign out</span>
            <span className="account__row-sub">
              Signing out here clears this browser only. Your recordings and baseline stay
              exactly as they are.
            </span>
          </div>
          <div className="account__actions">
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Icon name="logout" />}
              onClick={() => void signOut()}
            >
              Sign out
            </Button>
          </div>
        </div>
      </Card>

      <p className="shell__trust account__trust">
        Wellness and self-awareness tool. Not a medical device.
      </p>
    </div>
  )
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="detail-grid__item">
      <dt className="record__label">{label}</dt>
      <dd className={`record__value${mono ? ' mono-cell' : ''}`}>{value}</dd>
    </div>
  )
}
